import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEdit, FaPlus, FaArrowLeft, FaSearch, FaTrash, FaList } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchSubCategories, deleteSubCategory, clearError, clearSuccess } from '../../../redux/Slices/AdminCategoriesSlice';
import ConfirmModal from '../../../shared/components/ConfirmModal';

const AdminSubCategories = () => {
    const { setTitle } = useTitle();
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { subCategories, subCategoriesLoading: loading, updating, error, success } = useSelector((state) => state.adminCategories);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });

    useEffect(() => {
        setTitle('إدارة الفئات الفرعية');
    }, [setTitle]);

    useEffect(() => {
        dispatch(fetchSubCategories({ categoryId, search: searchTerm }));
    }, [dispatch, categoryId, searchTerm]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            toast.success(success);
            dispatch(clearSuccess());
            dispatch(fetchSubCategories({ categoryId, search: searchTerm }));
        }
    }, [success, dispatch, categoryId, searchTerm]);

    const handleAdd = () => {
        navigate(`/admin-categories/${categoryId}/sub-categories/add`);
    };

    const handleViewSubSubCategories = (subCategoryId) => {
        navigate(`/admin-categories/${subCategoryId}/sub-sub-categories`);
    };

    const handleEdit = (subCategoryId) => {
        navigate(`/admin-categories/${categoryId}/sub-categories/${subCategoryId}/edit`);
    };

    const handleDelete = (subCategoryId) => {
        setConfirmModal({
            isOpen: true,
            data: subCategoryId,
            message: 'هل أنت متأكد من حذف هذه الفئة الفرعية؟',
            title: 'تأكيد الحذف'
        });
    };

    const executeDelete = () => {
        dispatch(deleteSubCategory({ categoryId, subCategoryId: confirmModal.data }));
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary d-flex align-items-center"
                            onClick={() => navigate('/admin-categories')}
                            style={{ borderRadius: '8px' }}>
                            <FaArrowLeft className="mx-2" />
                            العودة للفئات
                        </motion.button>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white d-flex align-items-center"
                        onClick={handleAdd}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaPlus className="mx-2" />
                        إضافة فئة فرعية
                    </motion.button>
                </div>

                {/* Search */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">البحث</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ابحث عن فئة فرعية..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <span className="input-group-text" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                                        <FaSearch style={{ color: 'white' }} />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SubCategories Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الفئات الفرعية...</p>
                            </div>
                        ) : !Array.isArray(subCategories) || subCategories.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="text-muted">لا توجد فئات فرعية</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>كود الفئة الفرعية</th>
                                            <th>الاسم بالعربية</th>
                                            <th>الصورة</th>
                                            <th>الاسم بالإنجليزية</th>
                                            <th>الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subCategories.map((subCategory, index) => (
                                            <tr key={subCategory.id}>
                                                <td>{subCategory?.id}</td>
                                                <td>
                                                    <div className="fw-medium">{subCategory.name?.ar || 'غير محدد'}</div>
                                                </td>
                                                <td>
                                                    {subCategory.image_url || subCategory.image ? (
                                                        <img
                                                            src={subCategory.image_url || subCategory.image}
                                                            alt={subCategory.name?.ar || 'category'}
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td>{subCategory.name?.en || 'غير محدد'}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-sm text-white"
                                                            onClick={() => handleEdit(subCategory.id)}
                                                            disabled={updating}
                                                            title="تعديل"
                                                            style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                                            <FaEdit />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(subCategory.id)}
                                                            disabled={updating}
                                                            title="حذف">
                                                            <FaTrash />
                                                        </motion.button>
                                                        {/* إضافة زر Sub-Sub Categories للفئة رقم 3 فقط */}
                                                        {categoryId === '3' && (
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-subsubcategories"
                                                                onClick={() => handleViewSubSubCategories(subCategory.id)}
                                                                title="الفئات الفرعية للفئات الفرعية">
                                                                <FaList />
                                                            </motion.button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, data: null })}
                onConfirm={executeDelete}
                title="تأكيد الحذف"
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="حذف"
                cancelText="إلغاء"
                confirmButtonColor="#dc3545"
                type="danger"
            />
        </div>
    );
};

export default AdminSubCategories;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
    /* تصميم زر Sub-Sub Categories بنفس تصميم صفحة الفئات الرئيسية */
    .btn-subsubcategories {
        border: none;
        color: white !important;
        padding: 0.25rem;
        border-radius: 8px;
        font-size: 1.2rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        width: 36px;
        height: 36px;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
    }

    .btn-subsubcategories:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .btn-subsubcategories * {
        color: white !important;
    }
`;
document.head.appendChild(style);
